import type { ProCompany } from './repository/proCompaniesRepository'

/**
 * Where a Skida PRO company stands — 21 Oct 2026.
 *
 * One ladder, asked for by sales, so the cards above the companies table and
 * the badge on each row are the same count of the same thing:
 *
 *   signedIn    somebody is in, and nothing else has happened
 *   registered  the company filled in its billing profile
 *   trial       it has built at least one route or tour
 *   signedUp    at least one seat is paid for
 *   abandoned   nothing has happened for three months, and nothing is paid
 *
 * Read top-down from the furthest step: a paid seat wins over everything, and
 * a company that went quiet is abandoned whatever it had reached — unless it
 * pays, because a paying customer who is quiet is still a customer. Trial is
 * registered plus a route or tour, so a company that builds without filling in
 * its billing profile is still signed in.
 *
 * Everything here comes from the product project, through the same load the
 * table renders. Nothing is a CRM field.
 */
export type ProState = 'signedIn' | 'registered' | 'trial' | 'signedUp' | 'abandoned'

export const PRO_STATES: { key: ProState; label: string; hint: string; color: string }[] = [
  {
    key: 'signedIn',
    label: 'Signed in',
    hint: 'A workspace with at least one guide, and no billing profile filled in yet — whatever it has built.',
    color: 'var(--color-grey)',
  },
  {
    key: 'registered',
    label: 'Registered',
    hint: 'The billing profile is filled in, but no routes or tours yet. A logo, tagline or email alone does not count.',
    color: 'var(--color-indigo)',
  },
  {
    key: 'trial',
    label: 'Trial',
    hint: 'Billing profile filled in and at least one route or tour built, and no paid seat.',
    color: 'var(--color-primary)',
  },
  {
    key: 'signedUp',
    label: 'Signed up',
    hint: 'At least one guide on a paid Skida PRO agreement (ePay, state SUCCESS, inside its period). A promo grant is not a paid seat.',
    color: 'var(--color-success)',
  },
  {
    key: 'abandoned',
    label: 'Abandoned',
    hint: 'No activity for three months and no paid seat. Activity is the latest of: the company record changing, a guide joining, signing in or moving through onboarding, and a route or tour being made or edited.',
    color: 'var(--color-warning)',
  },
]

export const STATE_OF = Object.fromEntries(PRO_STATES.map((s) => [s.key, s])) as Record<
  ProState,
  (typeof PRO_STATES)[number]
>

const QUIET_MS = 91 * 24 * 60 * 60 * 1000

/** The latest sign of life anywhere in the company, or null when there is none. */
export function lastActivityAt(c: ProCompany): string | null {
  const stamps = [
    c.createdAt,
    c.updatedAt,
    c.billing?.updatedAt ?? null,
    c.lastWorkAt,
    ...c.members.flatMap((m) => [m.joinedAt, m.lastSignInAt, m.onboardingUpdatedAt]),
  ].filter((s): s is string => Boolean(s))
  return stamps.length ? stamps.reduce((a, b) => (a > b ? a : b)) : null
}

/**
 * Registered means the billing profile is filled in — 21 Oct 2026, from sales.
 * A profile the form left empty is not filled in: it needs a name, an address
 * or a VAT number.
 */
function hasBillingProfile(c: ProCompany): boolean {
  const b = c.billing
  return Boolean(b && (b.companyName || b.addressLines.length || b.city || b.vatNumber))
}

/**
 * The company's state, or null while its routes and tours are still being
 * counted — a company cannot be told from a trial before then.
 */
export function companyState(c: ProCompany, nowMs = Date.now()): ProState | null {
  if (c.members.some((m) => m.access === 'paid')) return 'signedUp'
  const last = lastActivityAt(c)
  if (last && nowMs - Date.parse(last) > QUIET_MS) return 'abandoned'
  if (c.routes === null || c.tours === null) return null
  if (!hasBillingProfile(c)) return 'signedIn'
  return c.routes + c.tours > 0 ? 'trial' : 'registered'
}
